import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import type { RealtimeChannel } from '@supabase/supabase-js';

// STUN servers for NAT traversal
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

// Remote session types
export interface RemoteSession {
  id: string;
  organization_id: string;
  location_id: string;
  register_id: string | null;
  admin_user_id: string;
  cashier_user_id: string | null;
  status: 'pending' | 'connecting' | 'active' | 'ended' | 'rejected' | 'expired';
  initiated_by: 'admin' | 'cashier';
  session_type: 'view_only' | 'full_control';
  connection_quality: 'excellent' | 'good' | 'fair' | 'poor' | null;
  created_at: string;
  accepted_at: string | null;
  connected_at: string | null;
  ended_at: string | null;
  expires_at: string | null;
  end_reason: string | null;
  notes: string | null;
  admin_device_info: Record<string, unknown>;
  cashier_device_info: Record<string, unknown>;
}

export interface SupportRequest {
  id: string;
  organization_id: string;
  location_id: string;
  location_name?: string;
  register_id: string | null;
  register_name?: string | null;
  requester_id: string;
  requester_name?: string;
  status: 'pending' | 'acknowledged' | 'in_progress' | 'resolved' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  message: string | null;
  category: 'technical' | 'transaction' | 'customer' | 'hardware' | 'other' | null;
  responder_id: string | null;
  response_message: string | null;
  remote_session_id: string | null;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  expires_at: string | null;
}

// Input event types for remote control
export interface RemoteInputEvent {
  type: 'mouse' | 'keyboard' | 'scroll';
  // Mouse events
  mouseType?: 'move' | 'down' | 'up' | 'click' | 'dblclick';
  button?: number;
  x?: number;
  y?: number;
  // Keyboard events
  keyType?: 'down' | 'up' | 'press';
  key?: string;
  code?: string;
  modifiers?: {
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
  };
  // Scroll events
  deltaX?: number;
  deltaY?: number;
}

// Signaling message types
type SignalingMessage =
  | { type: 'offer'; sdp: RTCSessionDescriptionInit }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit }
  | { type: 'ice-candidate'; candidate: RTCIceCandidateInit }
  | { type: 'input'; event: RemoteInputEvent }
  | { type: 'end-session'; reason: string };

// Connection state
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'failed';

/**
 * Hook for managing remote sessions as a viewer (admin/manager).
 * Use this when connecting to view/control a cashier's register.
 */
export function useRemoteSessionViewer() {
  const { user, profile } = useAuth();
  const { currentLocation } = useLocationStore();
  const queryClient = useQueryClient();

  // State
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [currentSession, setCurrentSession] = useState<RemoteSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs for WebRTC
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const signalingChannelRef = useRef<RealtimeChannel | null>(null);
  const iceCandidateQueueRef = useRef<RTCIceCandidateInit[]>([]);

  // Clean up WebRTC resources
  const cleanup = useCallback(() => {
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (signalingChannelRef.current) {
      supabase.removeChannel(signalingChannelRef.current);
      signalingChannelRef.current = null;
    }
    iceCandidateQueueRef.current = [];
    setRemoteStream(null);
    setConnectionState('disconnected');
  }, []);

  // Set up signaling channel for a session
  const setupSignaling = useCallback((sessionId: string) => {
    const channel = supabase.channel(`remote-session:${sessionId}`, {
      config: { broadcast: { self: false } },
    });

    channel.on('broadcast', { event: 'signaling' }, async ({ payload }) => {
      const message = payload as SignalingMessage;
      const pc = peerConnectionRef.current;

      // Handle "ready" signal from cashier - they're ready to receive the offer
      if ((message as any).type === 'ready') {
        // If we have a pending session that needs to connect, send the offer now
        if (currentSession && connectionState === 'connecting' && pc) {
          try {
            // Add transceivers for receiving video/audio
            pc.addTransceiver('video', { direction: 'recvonly' });
            pc.addTransceiver('audio', { direction: 'recvonly' });

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            await channel.send({
              type: 'broadcast',
              event: 'signaling',
              payload: { type: 'offer', sdp: offer } as SignalingMessage,
            });
          } catch (err) {
            console.error('Error sending offer:', err);
          }
        }
        return;
      }

      if (!pc) return;

      try {
        if (message.type === 'answer') {
          await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));
          // Flush queued ICE candidates now that remote description is set
          for (const candidate of iceCandidateQueueRef.current) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          iceCandidateQueueRef.current = [];
        } else if (message.type === 'ice-candidate' && message.candidate) {
          // Queue ICE candidates if remote description not yet set
          if (!pc.remoteDescription) {
            iceCandidateQueueRef.current.push(message.candidate);
          } else {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          }
        } else if (message.type === 'end-session') {
          cleanup();
          setCurrentSession(null);
        }
      } catch (err) {
        console.error('Error handling signaling message:', err);
      }
    });

    channel.subscribe();
    signalingChannelRef.current = channel;

    return channel;
  }, [cleanup, currentSession, connectionState]);

  // Send signaling message
  const sendSignaling = useCallback(async (_sessionId: string, message: SignalingMessage) => {
    const channel = signalingChannelRef.current;
    if (channel) {
      await channel.send({
        type: 'broadcast',
        event: 'signaling',
        payload: message,
      });
    }
  }, []);

  // Create peer connection and initiate connection
  // Note: The offer is NOT sent here - it's sent when we receive the "ready" signal from the cashier
  const initiateConnection = useCallback(async (session: RemoteSession) => {
    cleanup();
    setConnectionState('connecting');
    setError(null);

    try {
      // Set up signaling first
      setupSignaling(session.id);

      // Create peer connection
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peerConnectionRef.current = pc;

      // Handle incoming tracks (screen share from cashier)
      pc.ontrack = (event) => {
        console.log('Received remote track:', event.track.kind);
        setRemoteStream(event.streams[0]);
        setConnectionState('connected');
      };

      // Create data channel for remote control
      const dataChannel = pc.createDataChannel('control', {
        ordered: true,
      });
      dataChannelRef.current = dataChannel;

      dataChannel.onopen = () => {
        console.log('Data channel opened');
      };

      dataChannel.onclose = () => {
        console.log('Data channel closed');
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignaling(session.id, {
            type: 'ice-candidate',
            candidate: event.candidate.toJSON(),
          });
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log('Connection state:', pc.connectionState);
        if (pc.connectionState === 'failed') {
          setConnectionState('failed');
          setError('Connection failed');
        } else if (pc.connectionState === 'disconnected') {
          setConnectionState('disconnected');
        }
      };

      // Don't send offer immediately - wait for "ready" signal from cashier
      // This ensures the cashier's signaling channel is subscribed before we send
      console.log('Waiting for cashier ready signal...');

    } catch (err) {
      console.error('Error initiating connection:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setConnectionState('failed');
      cleanup();
    }
  }, [cleanup, setupSignaling, sendSignaling]);

  // Send remote input event
  const sendInput = useCallback((event: RemoteInputEvent) => {
    const dc = dataChannelRef.current;
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify({ type: 'input', event }));
    }
  }, []);

  // Request a new remote session (admin-initiated)
  const requestSession = useMutation({
    mutationFn: async ({
      locationId,
      registerId,
      cashierUserId,
      sessionType = 'full_control',
    }: {
      locationId: string;
      registerId?: string;
      cashierUserId?: string;
      sessionType?: 'view_only' | 'full_control';
    }) => {
      const { data, error } = await supabase.rpc('create_remote_session_request', {
        p_location_id: locationId,
        p_register_id: registerId || null,
        p_cashier_user_id: cashierUserId || null,
        p_session_type: sessionType,
      });

      if (error) throw error;
      return data as RemoteSession;
    },
    onSuccess: (session) => {
      setCurrentSession(session);
      // Set up signaling and wait for cashier to accept
      setupSignaling(session.id);
      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // Connect to a pending support request
  const connectToSupport = useMutation({
    mutationFn: async ({
      requestId,
      sessionType = 'full_control',
    }: {
      requestId: string;
      sessionType?: 'view_only' | 'full_control';
    }) => {
      const { data, error } = await supabase.rpc('connect_to_support_request', {
        p_request_id: requestId,
        p_session_type: sessionType,
      });

      if (error) throw error;
      return data as RemoteSession;
    },
    onSuccess: (session) => {
      setCurrentSession(session);
      initiateConnection(session);
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // End current session
  const endSession = useMutation({
    mutationFn: async (reason: string = 'admin_ended') => {
      if (!currentSession) throw new Error('No active session');

      const { data, error } = await supabase.rpc('end_remote_session', {
        p_session_id: currentSession.id,
        p_reason: reason,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (currentSession) {
        sendSignaling(currentSession.id, { type: 'end-session', reason: 'admin_ended' });
      }
      cleanup();
      setCurrentSession(null);
      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // Query for pending support requests
  const supportRequests = useQuery({
    queryKey: ['support-requests', currentLocation?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_pending_support_requests', {
        p_location_id: currentLocation?.id || null,
      });

      if (error) throw error;
      return data as SupportRequest[];
    },
    enabled: !!user && !!profile && ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(profile.role),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Query for my active sessions
  const mySessions = useQuery({
    queryKey: ['remote-sessions', 'my-sessions'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_remote_sessions');
      if (error) throw error;
      return data as RemoteSession[];
    },
    enabled: !!user,
  });

  // Subscribe to session updates
  useEffect(() => {
    if (!currentSession) return;

    const channel = supabase
      .channel(`session-updates:${currentSession.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'remote_sessions',
          filter: `id=eq.${currentSession.id}`,
        },
        (payload) => {
          const updated = payload.new as RemoteSession;
          setCurrentSession(updated);

          if (updated.status === 'connecting' && currentSession.status === 'pending') {
            // Cashier accepted, initiate WebRTC connection
            initiateConnection(updated);
          } else if (updated.status === 'ended' || updated.status === 'rejected') {
            cleanup();
            setCurrentSession(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentSession, initiateConnection, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    connectionState,
    remoteStream,
    currentSession,
    error,

    // Data
    supportRequests: supportRequests.data || [],
    supportRequestsLoading: supportRequests.isLoading,
    mySessions: mySessions.data || [],

    // Actions
    requestSession,
    connectToSupport,
    endSession,
    sendInput,

    // Helpers
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    hasActiveSession: !!currentSession && ['pending', 'connecting', 'active'].includes(currentSession.status),
  };
}

/**
 * Hook for managing remote sessions as a host (cashier).
 * Use this when sharing your screen with an admin.
 */
export function useRemoteSessionHost() {
  const { user } = useAuth();
  const { currentLocation } = useLocationStore();
  const queryClient = useQueryClient();

  // State
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [currentSession, setCurrentSession] = useState<RemoteSession | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<RemoteSession | null>(null);

  // Refs for WebRTC
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const signalingChannelRef = useRef<RealtimeChannel | null>(null);
  const iceCandidateQueueRef = useRef<RTCIceCandidateInit[]>([]);

  // Clean up WebRTC resources
  const cleanup = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (signalingChannelRef.current) {
      supabase.removeChannel(signalingChannelRef.current);
      signalingChannelRef.current = null;
    }
    iceCandidateQueueRef.current = [];
    setLocalStream(null);
    setConnectionState('disconnected');
  }, [localStream]);

  // Handle incoming remote input events
  const handleRemoteInput = useCallback((event: RemoteInputEvent) => {
    // For web, we'll dispatch synthetic events
    // For Electron, we'll use IPC to simulate system-level input
    const isElectron = typeof window !== 'undefined' && (window as any).electronAPI?.isElectron;

    if (isElectron) {
      // Send to Electron main process for system-level input
      (window as any).electronAPI?.remoteControl?.simulateInput?.(event);
    } else {
      // Web: dispatch synthetic events (limited to the app viewport)
      const target = document.elementFromPoint(event.x || 0, event.y || 0);
      if (!target) return;

      if (event.type === 'mouse') {
        const mouseEvent = new MouseEvent(
          event.mouseType === 'move' ? 'mousemove' :
          event.mouseType === 'down' ? 'mousedown' :
          event.mouseType === 'up' ? 'mouseup' :
          event.mouseType === 'click' ? 'click' :
          event.mouseType === 'dblclick' ? 'dblclick' : 'mousemove',
          {
            bubbles: true,
            cancelable: true,
            clientX: event.x,
            clientY: event.y,
            button: event.button || 0,
          }
        );
        target.dispatchEvent(mouseEvent);
      } else if (event.type === 'keyboard') {
        const keyEvent = new KeyboardEvent(
          event.keyType === 'down' ? 'keydown' :
          event.keyType === 'up' ? 'keyup' : 'keypress',
          {
            bubbles: true,
            cancelable: true,
            key: event.key,
            code: event.code,
            ctrlKey: event.modifiers?.ctrl,
            altKey: event.modifiers?.alt,
            shiftKey: event.modifiers?.shift,
            metaKey: event.modifiers?.meta,
          }
        );
        target.dispatchEvent(keyEvent);
      } else if (event.type === 'scroll') {
        target.dispatchEvent(new WheelEvent('wheel', {
          bubbles: true,
          deltaX: event.deltaX || 0,
          deltaY: event.deltaY || 0,
        }));
      }
    }
  }, []);

  // Check if screen sharing is supported on this device
  const isScreenShareSupported = useCallback((): boolean => {
    const isElectron = typeof window !== 'undefined' && (window as any).electronAPI?.isElectron;
    if (isElectron) return true;
    
    // Check for getDisplayMedia support (not available on iOS/iPadOS)
    return typeof navigator !== 'undefined' && 
           typeof navigator.mediaDevices !== 'undefined' && 
           typeof navigator.mediaDevices.getDisplayMedia === 'function';
  }, []);

  // Start screen sharing
  const startScreenShare = useCallback(async (): Promise<MediaStream> => {
    const isElectron = typeof window !== 'undefined' && (window as any).electronAPI?.isElectron;

    if (isElectron) {
      // Use Electron's desktopCapturer
      const sources = await (window as any).electronAPI?.remoteControl?.getScreenSources?.();
      if (!sources || sources.length === 0) {
        throw new Error('No screen sources available');
      }

      // Get the entire screen (first source is usually the primary display)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          // @ts-expect-error - Electron-specific constraint
          mandatory: {
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: sources[0].id,
          },
        },
      });

      return stream;
    } else {
      // Check if screen sharing is supported on this device
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen sharing is not supported on this device. Remote sessions require a desktop browser or the BlazePOS desktop app. iPads and mobile devices cannot share their screen.');
      }

      // Use standard getDisplayMedia for web
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor',
        } as MediaTrackConstraints,
        audio: false,
      });

      return stream;
    }
  }, []);

  // Set up signaling channel
  const setupSignaling = useCallback((sessionId: string) => {
    const channel = supabase.channel(`remote-session:${sessionId}`, {
      config: { broadcast: { self: false } },
    });

    channel.on('broadcast', { event: 'signaling' }, async ({ payload }) => {
      const message = payload as SignalingMessage;
      const pc = peerConnectionRef.current;

      if (!pc) return;

      try {
        if (message.type === 'offer') {
          console.log('Received offer from admin');
          await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));

          // Flush queued ICE candidates now that remote description is set
          for (const candidate of iceCandidateQueueRef.current) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          iceCandidateQueueRef.current = [];

          // Start screen sharing and add tracks
          const stream = await startScreenShare();
          setLocalStream(stream);

          stream.getTracks().forEach(track => {
            console.log('Adding track:', track.kind);
            pc.addTrack(track, stream);
          });

          // Create and send answer
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          console.log('Sending answer to admin');
          await channel.send({
            type: 'broadcast',
            event: 'signaling',
            payload: { type: 'answer', sdp: answer } as SignalingMessage,
          });

          // Mark session as connected
          await supabase.rpc('mark_session_connected', { p_session_id: sessionId });
          setConnectionState('connected');
        } else if (message.type === 'ice-candidate' && message.candidate) {
          // Queue ICE candidates if remote description not yet set
          if (!pc.remoteDescription) {
            iceCandidateQueueRef.current.push(message.candidate);
          } else {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          }
        } else if (message.type === 'end-session') {
          cleanup();
          setCurrentSession(null);
        }
      } catch (err) {
        console.error('Error handling signaling message:', err);
        setError(err instanceof Error ? err.message : 'Connection error');
      }
    });

    // Subscribe and send "ready" signal once subscription is active
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Cashier signaling ready, sending ready signal');
        // Tell the admin we're ready to receive the offer
        channel.send({
          type: 'broadcast',
          event: 'signaling',
          payload: { type: 'ready' },
        });
      }
    });
    signalingChannelRef.current = channel;

    return channel;
  }, [startScreenShare, cleanup]);

  // Send signaling message
  const sendSignaling = useCallback(async (_sessionId: string, message: SignalingMessage) => {
    const channel = signalingChannelRef.current;
    if (channel) {
      await channel.send({
        type: 'broadcast',
        event: 'signaling',
        payload: message,
      });
    }
  }, []);

  // Initialize peer connection for hosting
  const initializePeerConnection = useCallback((sessionId: string) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peerConnectionRef.current = pc;

    // Handle data channel (for receiving control input)
    pc.ondatachannel = (event) => {
      const dc = event.channel;
      dataChannelRef.current = dc;

      dc.onmessage = (msgEvent) => {
        try {
          const data = JSON.parse(msgEvent.data);
          if (data.type === 'input') {
            handleRemoteInput(data.event);
          }
        } catch (err) {
          console.error('Error parsing data channel message:', err);
        }
      };
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignaling(sessionId, {
          type: 'ice-candidate',
          candidate: event.candidate.toJSON(),
        });
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'failed') {
        setConnectionState('failed');
        setError('Connection failed');
      } else if (pc.connectionState === 'disconnected') {
        setConnectionState('disconnected');
      } else if (pc.connectionState === 'connected') {
        setConnectionState('connected');
      }
    };

    return pc;
  }, [handleRemoteInput, sendSignaling]);

  // Accept a remote session request from admin
  const acceptSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { data, error } = await supabase.rpc('accept_remote_session', {
        p_session_id: sessionId,
      });

      if (error) throw error;
      return data as RemoteSession;
    },
    onSuccess: (session) => {
      setCurrentSession(session);
      setPendingRequest(null);
      setConnectionState('connecting');

      // Initialize peer connection and signaling
      initializePeerConnection(session.id);
      setupSignaling(session.id);

      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // Reject a remote session request
  const rejectSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { data, error } = await supabase.rpc('reject_remote_session', {
        p_session_id: sessionId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setPendingRequest(null);
      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // Create a support request (cashier-initiated)
  const createSupportRequest = useMutation({
    mutationFn: async ({
      message,
      category = 'other',
      priority = 'normal',
      registerId,
    }: {
      message?: string;
      category?: 'technical' | 'transaction' | 'customer' | 'hardware' | 'other';
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      registerId?: string;
    }) => {
      if (!currentLocation?.id) throw new Error('No location selected');

      const { data, error } = await supabase.rpc('create_support_request', {
        p_location_id: currentLocation.id,
        p_register_id: registerId || null,
        p_message: message || null,
        p_category: category,
        p_priority: priority,
      });

      if (error) throw error;
      return data as SupportRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
    },
  });

  // Cancel my support request
  const cancelSupportRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('support_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId)
        .eq('requester_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
    },
  });

  // End current session
  const endSession = useMutation({
    mutationFn: async () => {
      if (!currentSession) throw new Error('No active session');

      const { data, error } = await supabase.rpc('end_remote_session', {
        p_session_id: currentSession.id,
        p_reason: 'cashier_ended',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (currentSession) {
        sendSignaling(currentSession.id, { type: 'end-session', reason: 'cashier_ended' });
      }
      cleanup();
      setCurrentSession(null);
      queryClient.invalidateQueries({ queryKey: ['remote-sessions'] });
    },
  });

  // Query for my pending support request
  const myPendingRequest = useQuery({
    queryKey: ['support-requests', 'my-request', currentLocation?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_requests')
        .select('*')
        .eq('requester_id', user?.id)
        .eq('status', 'pending')
        .eq('location_id', currentLocation?.id)
        .maybeSingle();

      if (error) throw error;
      return data as SupportRequest | null;
    },
    enabled: !!user && !!currentLocation?.id,
  });

  // Subscribe to incoming session requests (admin-initiated)
  useEffect(() => {
    if (!user || !currentLocation?.id) return;

    const channel = supabase
      .channel(`incoming-sessions:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'remote_sessions',
          filter: `cashier_user_id=eq.${user.id}`,
        },
        (payload) => {
          const session = payload.new as RemoteSession;
          if (session.status === 'pending' && session.initiated_by === 'admin') {
            setPendingRequest(session);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'remote_sessions',
          filter: `cashier_user_id=eq.${user.id}`,
        },
        (payload) => {
          const session = payload.new as RemoteSession;
          if (currentSession?.id === session.id) {
            if (session.status === 'ended' || session.status === 'expired') {
              cleanup();
              setCurrentSession(null);
            } else {
              setCurrentSession(session);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentLocation?.id, currentSession, cleanup]);

  // Also listen for support requests being picked up
  useEffect(() => {
    if (!user || !myPendingRequest.data) return;

    const channel = supabase
      .channel(`my-support-request:${myPendingRequest.data.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_requests',
          filter: `id=eq.${myPendingRequest.data.id}`,
        },
        async (payload) => {
          const request = payload.new as SupportRequest;
          if (request.status === 'in_progress' && request.remote_session_id) {
            // Admin connected, get the session and set up connection
            const { data: session } = await supabase
              .from('remote_sessions')
              .select('*')
              .eq('id', request.remote_session_id)
              .single();

            if (session) {
              setCurrentSession(session as RemoteSession);
              setConnectionState('connecting');
              initializePeerConnection(session.id);
              setupSignaling(session.id);
            }
          }
          // Refetch my pending request
          queryClient.invalidateQueries({ queryKey: ['support-requests', 'my-request'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, myPendingRequest.data, initializePeerConnection, setupSignaling, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    connectionState,
    localStream,
    currentSession,
    pendingRequest,
    error,

    // Data
    myPendingRequest: myPendingRequest.data,
    hasPendingRequest: !!myPendingRequest.data,

    // Actions
    acceptSession,
    rejectSession,
    createSupportRequest,
    cancelSupportRequest,
    endSession,

    // Helpers
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    hasActiveSession: !!currentSession && ['connecting', 'active'].includes(currentSession.status),
    isBeingControlled: connectionState === 'connected' && currentSession?.session_type === 'full_control',
    isScreenShareSupported: isScreenShareSupported(),
  };
}

/**
 * Hook for subscribing to support request notifications (for managers/admins).
 * Use this to show real-time notifications when cashiers request help.
 */
export function useSupportRequestNotifications(locationId?: string) {
  const { user, profile } = useAuth();
  const [newRequests, setNewRequests] = useState<SupportRequest[]>([]);

  useEffect(() => {
    if (!user || !profile) return;
    if (!['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(profile.role)) return;

    const orgId = (profile as any).organization_id;
    if (!orgId) return;

    const channel = supabase
      .channel('support-request-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_requests',
          filter: locationId ? `location_id=eq.${locationId}` : undefined,
        },
        async (payload) => {
          const request = payload.new as SupportRequest;
          // Only notify for requests in our organization
          if (request.organization_id === orgId) {
            // Fetch additional details
            const { data: details } = await supabase
              .from('support_requests')
              .select(`
                *,
                location:locations(name),
                register:registers(name),
                requester:profiles(first_name, last_name)
              `)
              .eq('id', request.id)
              .single();

            if (details) {
              setNewRequests(prev => [
                {
                  ...request,
                  location_name: (details.location as any)?.name,
                  register_name: (details.register as any)?.name,
                  requester_name: details.requester
                    ? `${(details.requester as any).first_name || ''} ${(details.requester as any).last_name || ''}`.trim()
                    : 'Unknown',
                },
                ...prev,
              ]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, profile, locationId]);

  const dismissRequest = useCallback((requestId: string) => {
    setNewRequests(prev => prev.filter(r => r.id !== requestId));
  }, []);

  const clearAll = useCallback(() => {
    setNewRequests([]);
  }, []);

  return {
    newRequests,
    hasNewRequests: newRequests.length > 0,
    dismissRequest,
    clearAll,
  };
}

